import React from 'react';
import Header from '../components/ui/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import FAQ from '../components/ui/FAQ';
import { aboutFAQs } from '../data/faqs';
import Icon from '../components/AppIcon';

const AboutUs = () => {
  // Enhanced SEO Data
  const seoData = {
    title: 'About Us - OctalChip | Leading Web & Mobile App Development Company',
    description: 'Learn about OctalChip, a premier web and mobile app development company with 25+ agile developers, 10+ delivered projects, and 3+ years of experience. Meet our leadership team and discover our mission.',
    keywords: 'about OctalChip, web development company, mobile app development company, software development team, development agency, tech company, IT services company, software consulting, development team, agile developers, project experience, leadership team, company mission, technology solutions, digital transformation, custom software development, web applications, mobile applications, AI development, cloud services, DevOps, UI/UX design, enterprise software, SaaS development, e-commerce development, API development, database design, frontend development, backend development, full-stack development',
    canonical: 'https://www.octalchip.com/about',
    tags: ['about OctalChip', 'development team', 'leadership team', 'company mission'],
    additionalMeta: [
      { name: 'theme-color', content: '#00d690' },
      { name: 'mobile-web-app-capable', content: 'yes' }
    ],
    breadcrumbs: [
      { name: 'Home', url: 'https://www.octalchip.com/' },
      { name: 'About Us', url: 'https://www.octalchip.com/about' }
    ],

    // GEO-specific data
    geoData: {
      region: "IN-GJ",
      placename: "Gujarat, India",
      latitude: "23.0225",
      longitude: "72.5714"
    },
    localBusinessData: true,
    serviceArea: [
      { "@type": "Country", "name": "India" },
      { "@type": "Country", "name": "Germany" },
      { "@type": "Country", "name": "Italy" },
      { "@type": "Country", "name": "Dubai" },
      { "@type": "Country", "name": "United Kingdom" },
      { "@type": "Country", "name": "Netherlands" }
    ],

    // AEO-specific data
    faqData: aboutFAQs,

    // Voice search optimization
    voiceSearchKeywords: [
      "about OctalChip company",
      "web development team",
      "software development agency",
      "tech company leadership",
      "development team experience",
      "company mission statement",
      "development company history"
    ],
    conversationalQueries: [
      "Tell me about OctalChip company",
      "Who are the leaders at OctalChip?",
      "What is OctalChip's mission?",
      "How experienced is OctalChip team?",
      "Where is OctalChip located?"
    ],

    // Featured snippet optimization
    featuredSnippetContent: "OctalChip is a premier web and mobile app development company founded in 2022, with 25+ agile developers, 10+ delivered projects, and 3+ years of experience. Located in Gujarat, India, we serve clients globally.",
    
    // List data for featured snippets
    listData: {
      name: "OctalChip Leadership Team",
      description: "Meet the leadership team at OctalChip",
      items: [
        {
          name: "Rina Patel",
          description: "Director of Operations - overseeing day-to-day operations and project delivery",
          url: "https://www.octalchip.com/about#leadership"
        },
        {
          name: "Rajesh Kumar",
          description: "Technical Director - leading technical strategy and quality standards",
          url: "https://www.octalchip.com/about#leadership"
        },
        {
          name: "Priya Sharma",
          description: "Director of Client Relations - managing client relationships and satisfaction",
          url: "https://www.octalchip.com/about#leadership"
        },
        {
          name: "Anita Desai",
          description: "Director of Business Development - driving business growth and partnerships",
          url: "https://www.octalchip.com/about#leadership"
        }
      ]
    },
    structuredData: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About OctalChip",
      "description": "Learn about OctalChip, a premier web and mobile app development company with 25+ agile developers, 10+ delivered projects, and 3+ years of experience.",
      "url": "https://www.octalchip.com/about",
      "mainEntity": {
        "@type": "Organization",
        "name": "OctalChip",
        "url": "https://www.octalchip.com",
        "logo": "https://www.octalchip.com/favicon.svg",
        "description": "Leading web and mobile app development company offering AI-powered solutions, custom software development, cloud services, and digital transformation.",
        "foundingDate": "2021",
        "numberOfEmployees": "25",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "404, Opera point, kothariya",
          "addressLocality": "Gujarat",
          "addressCountry": "IN",
          "postalCode": "360022"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "email": "business@octalchip.com",
          "telephone": "+91-7574082582",
          "availableLanguage": "English"
        },
        "sameAs": [
          "https://linkedin.com/company/octalchip",
          "https://twitter.com/octalchip",
          "https://github.com/octalchip",
          "https://www.facebook.com/octalchip",
          "https://www.instagram.com/octalchiptech/"
        ],
        "employee": [
          {
            "@type": "Person",
            "name": "Rina Patel",
            "jobTitle": "Director of Operations",
            "description": "Overseeing day-to-day operations and ensuring smooth project delivery across all client engagements."
          },
          {
            "@type": "Person",
            "name": "Rajesh Kumar",
            "jobTitle": "Technical Director",
            "description": "Leading technical strategy and ensuring the highest quality standards in all development projects."
          },
          {
            "@type": "Person",
            "name": "Priya Sharma",
            "jobTitle": "Director of Client Relations",
            "description": "Managing client relationships and ensuring exceptional customer satisfaction throughout project lifecycles."
          },
          {
            "@type": "Person",
            "name": "Anita Desai",
            "jobTitle": "Director of Business Development",
            "description": "Driving business growth and strategic partnerships to expand OctalChip's market presence and opportunities."
          }
        ],
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "ratingCount": "150",
          "bestRating": "5",
          "worstRating": "1"
        },
        "knowsAbout": [
          "Web Development",
          "Mobile App Development", 
          "AI Development",
          "Cloud Computing",
          "DevOps",
          "UI/UX Design",
          "Workflow Automation",
          "Digital Transformation"
        ]
      }
    }
  };

  const teamMembers = [
    {
      name: 'Rina Patel',
      role: 'Director of Operations',
      description: 'Overseeing day-to-day operations and ensuring smooth project delivery across all client engagements.'
    },
    {
      name: 'Rajesh Kumar',
      role: 'Technical Director',
      description: 'Leading technical strategy and ensuring the highest quality standards in all development projects.'
    },
    {
      name: 'Priya Sharma',
      role: 'Director of Client Relations',
      description: 'Managing client relationships and ensuring exceptional customer satisfaction throughout project lifecycles.'
    },
    {
      name: 'Anita Desai',
      role: 'Director of Business Development',
      description: 'Driving business growth and strategic partnerships to expand OctalChip\'s market presence and opportunities.'
    }
  ];

  const values = [
    {
      icon: 'Target',
      title: 'Innovation First',
      description: 'We stay ahead of technology trends to deliver cutting-edge solutions that drive business growth.'
    },
    {
      icon: 'Users',
      title: 'Client-Centric',
      description: 'Your success is our priority. We build solutions that align with your business goals and user needs.'
    },
    {
      icon: 'Shield',
      title: 'Quality Assurance',
      description: 'Every project undergoes rigorous testing to ensure reliability, security, and performance.'
    },
    {
      icon: 'Clock',
      title: 'Timely Delivery',
      description: 'We respect deadlines and deliver projects on time without compromising on quality.'
    }
  ];

  const milestones = [
    { year: '2022', title: 'Company Founded', description: 'OctalChip was established with a vision to transform businesses through technology.' },
    { year: '2023', title: 'First 10 Projects', description: 'Successfully delivered 10+ projects across various industries.' },
    { year: '2024', title: 'Team Expansion', description: 'Grew to 25+ agile developers and expanded service offerings.' },
    { year: '2025', title: 'Industry Recognition', description: 'Received 5+ awards and served 9+ satisfied clients.' }
  ];

  return (
    <div className="min-h-screen bg-[#f4faf8]">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        ogImage={seoData.ogImage}
        tags={seoData.tags}
        additionalMeta={seoData.additionalMeta}
        breadcrumbs={seoData.breadcrumbs}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
        geoData={seoData.geoData}
        localBusinessData={seoData.localBusinessData}
        serviceArea={seoData.serviceArea}
        voiceSearchKeywords={seoData.voiceSearchKeywords}
        conversationalQueries={seoData.conversationalQueries}
        featuredSnippetContent={seoData.featuredSnippetContent}
        listData={seoData.listData}
      />
      <Header />
      
      {/* Hero Section */}
      <section id="hero" className="bg-white py-8 sm:py-12 md:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-6">
              About OctalChip
            </h1>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-6 sm:mb-8"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 leading-relaxed mb-6 sm:mb-8 px-4">
              We are a cutting-edge digital agency that transforms businesses through innovative web, mobile, cloud, and backend solutions. 
              Our mission is to empower businesses with technology that drives growth and success.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mt-8 sm:mt-12">
              <div className="text-center">
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-[#00d690] mb-1 sm:mb-2">25+</div>
                <div className="text-xs sm:text-sm md:text-base text-gray-600">Team Members</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-[#00d690] mb-1 sm:mb-2">10+</div>
                <div className="text-xs sm:text-sm md:text-base text-gray-600">Projects Delivered</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-[#00d690] mb-1 sm:mb-2">3+</div>
                <div className="text-xs sm:text-sm md:text-base text-gray-600">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-[#00d690] mb-1 sm:mb-2">9+</div>
                <div className="text-xs sm:text-sm md:text-base text-gray-600">Happy Clients</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section id="our-story" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 sm:mb-12 md:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">Our Story</h2>
              <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto"></div>
            </div>
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8 md:gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-6">From Vision to Reality</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Founded in 2022, OctalChip emerged from a simple yet powerful vision: to make cutting-edge technology 
                  accessible to businesses of all sizes. We started with a small team of passionate developers and designers 
                  who believed in the transformative power of digital solutions.
                </p>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Today, we've grown into a team of 25+ agile professionals, delivering innovative solutions across web, 
                  mobile, cloud, and backend technologies. Our journey has been marked by continuous learning, adaptation, 
                  and an unwavering commitment to client success.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  From our headquarters in Gujarat, India, we serve clients globally, helping them navigate the digital 
                  landscape and achieve their business objectives through technology-driven solutions.
                </p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <h4 className="text-xl font-bold text-gray-800 mb-6">Our Mission</h4>
                <p className="text-gray-600 leading-relaxed mb-6">
                  To empower businesses with innovative digital solutions that drive growth, optimize operations, 
                  and create meaningful impact in the digital world.
                </p>
                <h4 className="text-xl font-bold text-gray-800 mb-6">Our Vision</h4>
                <p className="text-gray-600 leading-relaxed">
                  To be the leading digital transformation partner for businesses worldwide, known for innovation, 
                  quality, and client success.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section id="values" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">Our Values</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={value.icon} size={32} className="text-[#00d690]" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section id="leadership" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">Our Leadership</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {teamMembers.map((member, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg text-center hover:shadow-xl transition-shadow">
                <div className="w-20 h-20 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="User" size={40} className="text-[#00d690]" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">{member.name}</h3>
                <p className="text-[#00d690] font-medium mb-3 text-sm">{member.role}</p>
                <p className="text-gray-600 leading-relaxed text-sm">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section id="timeline" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">Our Journey</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto"></div>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="space-y-6 sm:space-y-8">
              {milestones.map((milestone, index) => (
                <div key={index} className="flex items-center">
                  <div className="flex-shrink-0 w-16 h-16 bg-[#00d690] rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {milestone.year}
                  </div>
                  <div className="ml-8 flex-1">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{milestone.title}</h3>
                    <p className="text-gray-600">{milestone.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-[#f4faf8]">
        <div className="container mx-auto px-4 sm:px-6 md:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6">Ready to Work With Us?</h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
            Let's discuss your project and see how we can help bring your vision to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <a
              href="https://cal.com/octalchip/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#00d690] text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-[#00b876] transition-colors"
            >
              Book a Consultation
            </a>
            <a
              href="/contact"
              className="border-2 border-[#00d690] text-[#00d690] px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-[#00d690] hover:text-white transition-colors"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        faqs={aboutFAQs}
        title="About OctalChip FAQs"
        description="Common questions about our company, team, mission, and experience."
      />

      <Footer />
    </div>
  );
};

export default AboutUs; 